import * as Utils from "../Utils";
import { adminFetch, buildQueryString } from "./admin_api";

const getAuthToken = () => localStorage.getItem("adminToken");

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

const affiliateApi = (path, options = {}) =>
  adminFetch(`${Utils.API_URL}v1/affiliate${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

/**
 * GET /api/v1/affiliate/all
 */
export async function getAffiliateUsers(params = {}) {
  return affiliateApi(`/all${buildQueryString(params)}`, { method: "GET" });
}

/**
 * PATCH /api/v1/affiliate/{affiliateId}/status
 * Block → "suspended", Unblock → "active" (also allows "pending")
 */
export async function updateAffiliateStatus(affiliateId, status) {
  return affiliateApi(`/${affiliateId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

/**
 * DELETE /api/v1/affiliate/{affiliateId}
 * Soft-delete: removes access; data may be retained. Frees email/phone.
 */
export async function deleteAffiliate(affiliateId) {
  return affiliateApi(`/${affiliateId}`, {
    method: "DELETE",
  });
}

export const AFFILIATE_BULK_DELETE_MAX = 100;

/**
 * POST /api/v1/affiliate/bulk-delete
 * Body: { affiliateIds } (max 100)
 * Partial success: data.deleted[], data.skipped[{ affiliateId, reason }]
 */
export async function bulkDeleteAffiliates(affiliateIds) {
  return affiliateApi("/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ affiliateIds }),
  });
}

/**
 * PATCH /api/v1/affiliate/{affiliateId}/commission-rate
 */
export async function updateAffiliateCommissionRate(affiliateId, commissionRate) {
  return affiliateApi(`/${affiliateId}/commission-rate`, {
    method: "PATCH",
    body: JSON.stringify({ commissionRate: Number(commissionRate) }),
  });
}

export const COMMISSION_RATE_PRESETS = [5, 8, 10, 15, 20];

/**
 * GET /api/v1/affiliate/admin/commissions
 */
export async function getAdminCommissions(params = {}) {
  return affiliateApi(`/admin/commissions${buildQueryString(params)}`, { method: "GET" });
}

/**
 * GET /api/v1/affiliate/admin/commissions/:commissionId
 */
export async function getAdminCommissionDetails(commissionId) {
  return affiliateApi(`/admin/commissions/${commissionId}`, { method: "GET" });
}

/**
 * PATCH /api/v1/affiliate/admin/commissions/:commissionId/approve
 */
export async function approveAdminCommission(commissionId) {
  return affiliateApi(`/admin/commissions/${commissionId}/approve`, { method: "PATCH" });
}

/**
 * PATCH /api/v1/affiliate/admin/commissions/:commissionId/cancel
 */
export async function cancelAdminCommission(commissionId, reason) {
  return affiliateApi(`/admin/commissions/${commissionId}/cancel`, {
    method: "PATCH",
    body: JSON.stringify({ reason }),
  });
}

/**
 * POST /api/v1/affiliate/admin/commissions/bulk-approve
 */
export async function bulkApproveAdminCommissions(commissionIds) {
  return affiliateApi("/admin/commissions/bulk-approve", {
    method: "POST",
    body: JSON.stringify({ commissionIds }),
  });
}

/**
 * GET /api/v1/affiliate/admin/:affiliateId/payout-profile
 */
export async function getAdminAffiliatePayoutProfile(affiliateId) {
  return affiliateApi(`/admin/${affiliateId}/payout-profile`, { method: "GET" });
}

/**
 * GET /api/v1/affiliate/admin/payouts
 */
export async function getAdminPayouts(params = {}) {
  return affiliateApi(`/admin/payouts${buildQueryString(params)}`, { method: "GET" });
}

/**
 * GET /api/v1/affiliate/admin/payouts/:payoutId
 */
export async function getAdminPayoutDetails(payoutId) {
  return affiliateApi(`/admin/payouts/${payoutId}`, { method: "GET" });
}

/**
 * POST /api/v1/affiliate/admin/payouts
 */
export async function recordAdminPayout(payload) {
  return affiliateApi("/admin/payouts", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * GET /api/v1/affiliate/admin/withdrawals
 */
export async function getAdminWithdrawals(params = {}) {
  return affiliateApi(`/admin/withdrawals${buildQueryString(params)}`, { method: "GET" });
}

/**
 * PATCH /api/v1/affiliate/admin/withdrawals/:withdrawalId/approve
 */
export async function approveAdminWithdrawal(withdrawalId) {
  return affiliateApi(`/admin/withdrawals/${withdrawalId}/approve`, { method: "PATCH" });
}

/**
 * PATCH /api/v1/affiliate/admin/withdrawals/:withdrawalId/reject
 */
export async function rejectAdminWithdrawal(withdrawalId, reason) {
  return affiliateApi(`/admin/withdrawals/${withdrawalId}/reject`, {
    method: "PATCH",
    body: JSON.stringify({ reason }),
  });
}

/**
 * POST /api/v1/affiliate/admin/payouts/cashfree
 */
export async function initiateCashfreePayout(withdrawalId) {
  return affiliateApi("/admin/payouts/cashfree", {
    method: "POST",
    body: JSON.stringify({ withdrawalId }),
  });
}

/**
 * PATCH /api/v1/affiliate/admin/:affiliateId/kyc/verify
 */
export async function verifyAffiliateKyc(affiliateId, pan) {
  return affiliateApi(`/admin/${affiliateId}/kyc/verify`, {
    method: "PATCH",
    body: JSON.stringify({ pan }),
  });
}

export const normalizeWithdrawalList = (res) => {
  const candidates = [
    res?.data?.withdrawals,
    res?.data?.items,
    res?.withdrawals,
    res?.data,
  ];
  const list = candidates.find((item) => Array.isArray(item));
  return list || [];
};

export const WITHDRAWAL_STATUSES = [
  { value: "requested", label: "Requested" },
  { value: "approved", label: "Approved" },
  { value: "processing", label: "Processing" },
  { value: "paid", label: "Paid" },
  { value: "rejected", label: "Rejected" },
  { value: "failed", label: "Failed" },
];

export const getWithdrawalStatusBadge = (status) => {
  switch (status) {
    case "requested":
      return "warning";
    case "approved":
    case "processing":
      return "info";
    case "paid":
      return "success";
    case "rejected":
    case "failed":
      return "danger";
    default:
      return "secondary";
  }
};

export const AFFILIATE_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

export const normalizeCommissionList = (res) => {
  const candidates = [
    res?.data?.commissions,
    res?.data?.items,
    res?.commissions,
    res?.data,
  ];
  const list = candidates.find((item) => Array.isArray(item));
  return list || [];
};

export const normalizePayoutList = (res) => {
  const candidates = [
    res?.data?.payouts,
    res?.data?.items,
    res?.payouts,
    res?.data,
  ];
  const list = candidates.find((item) => Array.isArray(item));
  return list || [];
};

export const normalizePagination = (res) => {
  const p = res?.data?.pagination || res?.pagination;
  if (!p) return null;
  return {
    page: Number(p.page) || 1,
    pages: Number(p.pages) || Number(p.totalPages) || 1,
    total: Number(p.total) || 0,
    limit: Number(p.limit) || 10,
  };
};

export const formatCurrency = (amount, currency = "INR") => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const num = Number(amount);
  if (Number.isNaN(num)) return `${currency} ${amount}`;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(num);
};

export const COMMISSION_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
];

export const getCommissionStatusBadge = (status) => {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
      return "info";
    case "paid":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "secondary";
  }
};
